<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="visible" class="action-modal-overlay" @click.self="close">
        <transition name="modal-slide">
          <div v-if="visible" class="action-modal" :class="{ 'action-modal--mobile': isMobile }">
            <!-- Header -->
            <div class="action-modal__header">
              <h3>{{ title || $t('chat.actions') || 'Actions' }}</h3>
              <button class="action-modal__close" @click="close">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>

            <!-- Actions List -->
            <div class="action-modal__content">
              <!-- Normal Actions -->
              <div
                v-for="action in normalActions"
                :key="action.id"
                class="action-modal__item"
                :class="{ 'action-modal__item--disabled': action.disabled }"
                @click="handleActionClick(action)"
              >
                <div class="action-modal__item-icon" :class="action.iconClass">
                  <i :class="action.icon"></i>
                </div>
                <div class="action-modal__item-content">
                  <div class="action-modal__item-title">{{ action.title }}</div>
                  <div v-if="action.description" class="action-modal__item-description">
                    {{ action.description }}
                  </div>
                </div>
                <div v-if="action.badge" class="action-modal__item-badge">
                  {{ action.badge }}
                </div>
              </div>

              <!-- Dangerous Actions -->
              <div
                v-for="action in dangerousActions"
                :key="action.id"
                class="action-modal__item action-modal__item--danger"
                :class="{ 'action-modal__item--disabled': action.disabled }"
                @click="handleActionClick(action)"
              >
                <div class="action-modal__item-icon action-modal__item-icon--danger">
                  <i :class="action.icon"></i>
                </div>
                <div class="action-modal__item-content">
                  <div class="action-modal__item-title">{{ action.title }}</div>
                  <div v-if="action.description" class="action-modal__item-description">
                    {{ action.description }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Cancel Button (Mobile) -->
            <div v-if="isMobile" class="action-modal__footer">
              <button class="action-modal__cancel" @click="close">
                {{ $t('common.cancel') || 'Cancel' }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'ChatActionModal',
  
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    actions: {
      type: Array,
      default: () => [],
    },
  },
  
  emits: ['close', 'action'],
  
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    },
    normalActions() {
      return this.actions.filter(a => !a.dangerous);
    },
    dangerousActions() {
      return this.actions.filter(a => a.dangerous);
    },
  },
  
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  
  methods: {
    close() {
      this.$emit('close');
    },
    handleActionClick(action) {
      if (action.disabled) return;
      this.$emit('action', action.id);
      this.close();
    },
  },
};
</script>

<style scoped>
/* Overlay */
.action-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* Modal */
.action-modal {
  background: var(--bs-body-bg, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

/* Mobile Bottom Sheet */
.action-modal--mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: none;
  border-radius: 20px 20px 0 0;
  margin: 0;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* Header */
.action-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid var(--bs-border-color, #e5e5e5);
}

.action-modal__header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--bs-body-color, #333);
}

.action-modal__close {
  background: none;
  border: none;
  font-size: 20px;
  color: var(--bs-secondary-color, #6c757d);
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  transition: background 0.2s;
}

.action-modal__close:hover {
  background: var(--bs-tertiary-bg, #f5f5f5);
}

/* Content */
.action-modal__content {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

/* Action Item */
.action-modal__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.2s;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.action-modal__item:hover:not(.action-modal__item--disabled) {
  background: var(--bs-tertiary-bg, #f5f5f5);
}

.action-modal__item--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-modal__item--danger:hover {
  background: #fef2f2;
}

/* Item Icon */
.action-modal__item-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: #e7f1ff;
  color: #0d6efd !important;
  flex-shrink: 0;
}

.action-modal__item-icon--danger {
  background: #fef2f2;
  color: #dc2626 !important;
}

/* Item Content */
.action-modal__item-content {
  flex: 1;
  min-width: 0;
}

.action-modal__item-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--bs-body-color, #333);
  margin-bottom: 2px;
}

.action-modal__item-description {
  font-size: 13px;
  color: var(--bs-secondary-color, #6c757d);
}

/* Item Badge */
.action-modal__item-badge {
  font-size: 12px;
  padding: 2px 8px;
  background: var(--bs-primary, #0d6efd);
  color: white;
  border-radius: 10px;
  font-weight: 500;
}

/* Divider */
.action-modal__divider {
  display: flex;
  align-items: center;
  padding: 16px 20px 8px;
}

.action-modal__divider::before,
.action-modal__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--bs-border-color, #e5e5e5);
}

.action-modal__divider span {
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--bs-secondary-color, #6c757d);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Footer (Mobile) */
.action-modal__footer {
  padding: 12px 20px 20px;
  border-top: 1px solid var(--bs-border-color, #e5e5e5);
}

.action-modal__cancel {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  background: var(--bs-tertiary-bg, #f5f5f5);
  color: var(--bs-body-color, #333);
  cursor: pointer;
  transition: background 0.2s;
}

.action-modal__cancel:hover {
  background: var(--bs-secondary-bg, #e9ecef);
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: all 0.3s ease;
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Dark Mode */
[data-bs-theme="dark"] .action-modal {
  background: var(--bs-body-bg);
  border-color: var(--bs-border-color);
}

[data-bs-theme="dark"] .action-modal__header {
  border-color: var(--bs-border-color);
}

[data-bs-theme="dark"] .action-modal__header h3 {
  color: var(--bs-body-color);
}

[data-bs-theme="dark"] .action-modal__close {
  color: var(--bs-secondary-color);
}

[data-bs-theme="dark"] .action-modal__close:hover {
  background: var(--bs-tertiary-bg);
}

[data-bs-theme="dark"] .action-modal__item:hover:not(.action-modal__item--disabled) {
  background: var(--bs-tertiary-bg);
}

[data-bs-theme="dark"] .action-modal__item--danger:hover {
  background: #3d1515;
}

[data-bs-theme="dark"] .action-modal__item-icon {
  background: #1e3a5f;
  color: #60a5fa !important;
}

[data-bs-theme="dark"] .action-modal__item-icon--danger {
  background: #3d1515;
  color: #f87171 !important;
}

[data-bs-theme="dark"] .action-modal__item-title {
  color: var(--bs-body-color);
}

[data-bs-theme="dark"] .action-modal__item-description {
  color: var(--bs-secondary-color);
}

[data-bs-theme="dark"] .action-modal__divider::before,
[data-bs-theme="dark"] .action-modal__divider::after {
  background: var(--bs-border-color);
}

[data-bs-theme="dark"] .action-modal__divider span {
  color: var(--bs-secondary-color);
}

[data-bs-theme="dark"] .action-modal__footer {
  border-color: var(--bs-border-color);
}

[data-bs-theme="dark"] .action-modal__cancel {
  background: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
}

[data-bs-theme="dark"] .action-modal__cancel:hover {
  background: var(--bs-secondary-bg);
}

/* RTL Support */
[dir="rtl"] .action-modal__item {
  text-align: right;
}

[dir="rtl"] .action-modal__item-icon {
  margin-left: 0;
  margin-right: 12px;
}
</style>
